import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  useProject,
  useProjectSettings,
  useUpdateProjectSettings,
} from "@/hooks/api/use-projects";
import { Mail } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";

interface ProjectSetting {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  enabled: boolean;
}

export default function ProjectSettingsPage() {
  const { id } = useParams<{ id: string }>();
  const projectId = id ? parseInt(id) : 0;
  const { data: project, isLoading: isLoadingProject } = useProject(id!);
  const { data: projectSettings, isLoading: isLoadingProjectSettings } =
    useProjectSettings(projectId);
  const { mutate: updateProjectSettings } = useUpdateProjectSettings(projectId);

  const [localSettings, setLocalSettings] = useState({
    strategy_update_notifications_email:
      projectSettings?.strategy_update_notifications_email ?? false,
    competitor_new_ads_notifications_email:
      projectSettings?.competitor_new_ads_notifications_email ?? false,
  });

  // Update local settings when projectSettings loads
  useEffect(() => {
    if (projectSettings) {
      setLocalSettings({
        strategy_update_notifications_email:
          projectSettings.strategy_update_notifications_email,
        competitor_new_ads_notifications_email:
          projectSettings.competitor_new_ads_notifications_email,
      });
    }
  }, [projectSettings]);

  const settings: ProjectSetting[] = [
    {
      id: "strategy-notifications",
      title: "Weekly Strategy Updates",
      description: "Receive weekly email notifications for strategy updates",
      icon: <Mail className="h-5 w-5" />,
      enabled: localSettings.strategy_update_notifications_email,
    },
    {
      id: "competitor-new-ads-notifications",
      title: "Daily Competitor New Ads",
      description:
        "Receive daily email notifications for new ads launched by competitors",
      icon: <Mail className="h-5 w-5" />,
      enabled: localSettings.competitor_new_ads_notifications_email,
    },
  ];

  const handleToggle = (id: string) => {
    const isStrategyNotification = id === "strategy-notifications";
    const newSettings = {
      ...localSettings,
      strategy_update_notifications_email: isStrategyNotification
        ? !localSettings.strategy_update_notifications_email
        : localSettings.strategy_update_notifications_email,
      competitor_new_ads_notifications_email: !isStrategyNotification
        ? !localSettings.competitor_new_ads_notifications_email
        : localSettings.competitor_new_ads_notifications_email,
    };

    setLocalSettings(newSettings);
    updateProjectSettings(newSettings);
  };

  if (isLoadingProject || isLoadingProjectSettings) {
    return <div>Loading...</div>;
  }

  return (
    <Card className="p-6">
      <div className="space-y-6">
        <div className="space-y-2">
          <h2 className="text-2xl font-bold">Project Settings</h2>
          <p className="text-muted-foreground">
            Manage your project preferences and configurations for project:{" "}
            {project?.name}
          </p>
        </div>

        <div className="space-y-4">
          {settings.map((setting) => (
            <div
              key={setting.id}
              className="flex items-center justify-between space-x-4 rounded-lg border p-4"
            >
              <div className="flex items-start space-x-4">
                <div className="mt-1 text-muted-foreground">{setting.icon}</div>
                <div className="space-y-1">
                  <p className="font-medium">{setting.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {setting.description} {setting.enabled}
                  </p>
                </div>
              </div>
              <Switch
                checked={setting.enabled}
                onCheckedChange={() => handleToggle(setting.id)}
              />
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
